import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import {
  AddrLink, Card, CopyBtn, DetailRow, EmptyState, ErrorState, Pager, SkeletonRows,
  StatCard, TokenLogo, TxLink, TypeBadge, WatchButton, usePaged,
} from "@/components/common";
import { fmtCompact, fmtDate, fmtNum, fmtTokenAmount, fmtUsd, timeAgo } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

const RANGES = ["1H", "24H", "7D", "30D", "1Y", "ALL"];
const BURN = ["0x0000000000000000000000000000000000000000", "0x000000000000000000000000000000000000dead"];

const holderLabel = (item) => {
  const addr = ((item.address || {}).hash || "").toLowerCase();
  if (BURN.includes(addr)) return "Burn Address";
  if (item.gmscan_label) return item.gmscan_label;
  if (item.address?.name) return item.address.name;
  if (item.address?.is_contract) return "Smart Contract";
  return null;
};

function MetricChart({ data, dataKey, title, sub, fmt, testId }) {
  const points = (data || []).filter((d) => d[dataKey] !== null && d[dataKey] !== undefined);
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="text-[10px] text-muted-foreground">{sub}</p>
      {data === null ? (
        <div className="h-40 rounded-lg bg-muted animate-pulse mt-3" />
      ) : points.length < 2 ? (
        <EmptyState title="Not enough history yet" subtitle="GMScan records snapshots every few minutes — this chart fills in with real data only." />
      ) : (
        <div className="h-40 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points.map((d) => ({ ...d, t: new Date(d.ts * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) }))}>
              <XAxis dataKey="t" tick={{ fontSize: 9 }} stroke="currentColor" opacity={0.4} tickLine={false} axisLine={false} />
              <YAxis domain={["auto", "auto"]} tick={{ fontSize: 9 }} stroke="currentColor" opacity={0.4} tickLine={false} axisLine={false} width={62} tickFormatter={fmt} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => [fmt(v), title]} />
              <Line type="monotone" dataKey={dataKey} stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function HistoryCharts({ address, isStock }) {
  const [range, setRange] = useState("24H");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/tokens/${address}/price-history`, { params: { range } })
      .then((r) => setData(r.data.items || []))
      .catch(() => setData([]));
  }, [address, range]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold">History</h2>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)}
              className={`text-[10px] px-2 py-1 rounded-md border transition-colors duration-150 ${range === r ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <MetricChart testId="price-chart-card" data={data} dataKey="price" title="Price"
        sub={`${isStock ? "Reference price" : "Onchain market price"} · GMScan snapshots, real data only`} fmt={(v) => fmtUsd(v)} />
      <div className="grid sm:grid-cols-2 gap-4">
        <MetricChart testId="volume-chart-card" data={data} dataKey="volume_24h" title="24h Trading Volume"
          sub="Rolling 24h volume in USD" fmt={(v) => `$${fmtCompact(v)}`} />
        <MetricChart testId="holders-chart-card" data={data} dataKey="holders" title="Holders"
          sub="Number of unique holders over time" fmt={fmtCompact} />
      </div>
    </div>
  );
}

function HoldersTab({ address, token }) {
  const pager = usePaged((p) => api.get(`/tokens/${address}/holders`, { params: p }).then((r) => r.data), [address]);
  const [search, setSearch] = useState("");
  const supply = Number(token.total_supply || 0);
  const decimals = Number(token.decimals || 18);
  const rate = token.exchange_rate ? Number(token.exchange_rate) : null;

  const distribution = useMemo(() => {
    if (!pager.items.length || !supply) return null;
    const vals = pager.items.map((h) => Number(h.value || 0));
    const top10 = vals.slice(0, 10).reduce((a, b) => a + b, 0) / supply * 100;
    const top50 = vals.slice(0, 50).reduce((a, b) => a + b, 0) / supply * 100;
    return [
      { name: "Top 10 holders", value: Math.min(top10, 100) },
      { name: "Top 11–50", value: Math.max(Math.min(top50 - top10, 100), 0) },
      { name: "Other holders", value: Math.max(100 - top50, 0) },
    ];
  }, [pager.items, supply]);

  const filtered = pager.items.filter((h) => !search || ((h.address || {}).hash || "").toLowerCase().includes(search.toLowerCase()));
  const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))"];

  if (pager.loading) return <SkeletonRows n={10} />;
  if (pager.error) return <ErrorState />;
  if (!pager.items.length) return <EmptyState title="No holders found" />;

  return (
    <div className="p-4 space-y-4">
      {distribution && (
        <div className="grid sm:grid-cols-2 gap-4 items-center">
          <div className="h-44" data-testid="holder-distribution-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={distribution} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3} strokeWidth={0}>
                  {distribution.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => `${v.toFixed(2)}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {distribution.map((d, i) => (
              <div key={d.name} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} />
                {d.name} <span className="ml-auto font-mono">{d.value.toFixed(2)}%</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground pt-1">Distribution computed from the top holders on this page vs total supply.</p>
          </div>
        </div>
      )}

      <input data-testid="holders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search holder address…"
        className="w-full sm:w-72 bg-secondary border rounded-lg px-3 py-2 text-xs outline-none focus:border-primary" />

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
              <th className="px-3 py-2.5 font-medium">#</th>
              <th className="px-3 py-2.5 font-medium">Holder</th>
              <th className="px-3 py-2.5 font-medium">Label</th>
              <th className="px-3 py-2.5 font-medium text-right">Balance</th>
              <th className="px-3 py-2.5 font-medium text-right">% Supply</th>
              <th className="px-3 py-2.5 font-medium text-right">Est. Value</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h, i) => {
              const bal = Number(h.value || 0);
              const pct = supply ? (bal / supply) * 100 : null;
              const label = holderLabel(h);
              return (
                <tr key={i} className="table-row-hover border-b last:border-0" data-testid="holder-row">
                  <td className="px-3 py-3 text-xs text-muted-foreground">{(pager.page - 1) * 50 + i + 1}</td>
                  <td className="px-3 py-3"><AddrLink hash={h.address?.hash} chars={8} /></td>
                  <td className="px-3 py-3">{label ? <TypeBadge>{label}</TypeBadge> : <span className="text-[10px] text-muted-foreground">Unknown wallet</span>}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{fmtTokenAmount(h.value, decimals)}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{pct !== null ? `${pct < 0.01 ? "<0.01" : pct.toFixed(2)}%` : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{rate ? fmtUsd((bal / 10 ** decimals) * rate, 0) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager pager={pager} />
    </div>
  );
}

function TransfersTab({ address }) {
  const pager = usePaged((p) => api.get(`/tokens/${address}/transfers`, { params: p }).then((r) => r.data), [address]);
  if (pager.loading) return <SkeletonRows n={8} />;
  if (pager.error) return <ErrorState />;
  if (!pager.items.length) return <EmptyState title="No transfers found" />;
  return (
    <>
      <div className="divide-y">
        {pager.items.map((t, i) => (
          <div key={i} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="token-transfer-row">
            <TxLink hash={t.transaction_hash} chars={5} />
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <AddrLink hash={t.from?.hash} chars={4} /> → <AddrLink hash={t.to?.hash} chars={4} />
            </span>
            <span className="ml-auto font-mono text-xs">{fmtTokenAmount(t.total?.value, t.total?.decimals ?? t.token?.decimals)} {t.token?.symbol}</span>
            <span className="text-xs text-muted-foreground">{timeAgo(t.timestamp)}</span>
          </div>
        ))}
      </div>
      <Pager pager={pager} />
    </>
  );
}

export default function TokenPage() {
  const { address } = useParams();
  const [token, setToken] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("overview");
  usePageTitle(token ? `${token.name} (${token.symbol}) Price, Holders & Charts` : "Token", token ? `${token.name} price, holders, transfers and charts on Robinhood Chain.` : undefined);

  useEffect(() => {
    setToken(null); setErr(false); setTab("overview");
    api.get(`/tokens/${address}`).then((r) => setToken(r.data)).catch(() => setErr(true));
  }, [address]);

  if (err) return <ErrorState message="We could not find this token on Robinhood Chain. Please check the value and try again." />;
  if (!token) return <SkeletonRows n={8} h="h-14" />;

  const isStock = (token.name || "").toLowerCase().includes("robinhood token");
  const supply = fmtTokenAmount(token.total_supply, token.decimals);
  const tabs = ["overview", "transfers", "holders", "contract"];

  return (
    <div className="space-y-6 pt-8">
      <div className="flex flex-wrap items-start gap-4">
        <TokenLogo url={token.icon_url} symbol={token.symbol} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="token-name">{token.name}</h1>
            <TypeBadge>{token.symbol}</TypeBadge>
            <TypeBadge>{token.type}</TypeBadge>
            {isStock && <span className="inline-flex text-[10px] rounded-full px-2 py-0.5 bg-primary/15 text-primary font-semibold">Stock Token</span>}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <p className="font-mono text-xs text-muted-foreground truncate">{address}</p>
            <CopyBtn text={address} testId="copy-token-address" />
          </div>
        </div>
        <WatchButton type={isStock ? "stock" : "token"} value={address} label={token.symbol || token.name} />
      </div>

      {isStock && (
        <Card className="p-4 border-yellow-500/30 bg-yellow-500/5" data-testid="stock-disclaimer">
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Tokenized equity notice:</span> this blockchain token tracks a stock via
            Robinhood's tokenization program. Holding it is not the same as owning a traditional share through a broker — one token
            may not always equal one share due to corporate-action multipliers.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="token-price" label={isStock ? "Reference Price" : "Price"}
          value={token.exchange_rate ? fmtUsd(token.exchange_rate) : "Price unavailable"}
          sub={token.exchange_rate ? `Source: ${isStock ? "Robinhood feed via indexer" : "CoinGecko / onchain"} · updated live` : "No reliable price source"} />
        <StatCard testId="token-marketcap" label="Market Value" value={token.circulating_market_cap ? fmtUsd(token.circulating_market_cap, 0) : "—"} sub={`24h volume ${token.volume_24h ? fmtUsd(token.volume_24h, 0) : "—"}`} />
        <StatCard testId="token-holders" label="Holders" value={fmtCompact(token.holders_count)} sub={`${fmtCompact(token.counters?.transfers_count)} transfers`} />
        <StatCard testId="token-supply" label="Total Supply" value={supply} sub={`${token.decimals} decimals`} />
      </div>

      <HistoryCharts address={address} isStock={isStock} />

      <Card>
        <div className="flex gap-1 overflow-x-auto border-b px-2 pt-2">
          {tabs.map((t) => (
            <button key={t} data-testid={`token-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-2 text-xs capitalize whitespace-nowrap transition-colors duration-150 ${
                tab === t ? "text-primary border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground"
              }`}>
              {t}
            </button>
          ))}
        </div>
        {tab === "overview" && (
          <div className="px-4 sm:px-5">
            <DetailRow label="Contract Address"><span className="inline-flex items-center gap-2 font-mono text-xs break-all">{address} <CopyBtn text={address} /></span></DetailRow>
            <DetailRow label="Contract Creator">{token.creator_address_hash ? <AddrLink hash={token.creator_address_hash} chars={10} /> : "—"}</DetailRow>
            <DetailRow label="Creation Transaction">{token.creation_transaction_hash ? <TxLink hash={token.creation_transaction_hash} chars={12} /> : "—"}</DetailRow>
            <DetailRow label="Token Standard">{token.type}</DetailRow>
            <DetailRow label="Total Supply">{supply} {token.symbol}</DetailRow>
            <DetailRow label="Holders">{fmtNum(token.holders_count, { maximumFractionDigits: 0 })}</DetailRow>
          </div>
        )}
        {tab === "transfers" && <TransfersTab address={address} />}
        {tab === "holders" && <HoldersTab address={address} token={token} />}
        {tab === "contract" && (
          <div className="p-4">
            <p className="text-xs text-muted-foreground mb-3">View the full contract details, source code and activity on the contract page.</p>
            <a href={`/address/${address}`} data-testid="view-contract-link" className="text-xs text-primary hover:underline">Open contract page →</a>
          </div>
        )}
      </Card>
    </div>
  );
}
